import { ReactNode, useState, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { PanelLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import Header from './Header';

const Sidebar = lazy(() => import('./Sidebar'));

interface AppLayoutProps {
  children: ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  // Full view mode - hide sidebar and header by default for tools
  const [fullView, setFullView] = useState(true);

  // Landing page - no sidebar, full width
  if (isHome) {
    return (
      <div className="min-h-screen bg-background">
        {children}
      </div>
    );
  }

  // Tool pages - full view mode with floating toggle
  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar - hidden in full view mode */}
      {!fullView && (
        <Suspense fallback={<div className="w-16 border-r border-border bg-card" />}>
          <Sidebar expanded={sidebarExpanded} onToggle={() => setSidebarExpanded(!sidebarExpanded)} />
        </Suspense>
      )}

      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Header - hidden in full view mode */}
        {!fullView && <Header />}

        {/* Main content */}
        <main className={cn('flex-1 overflow-y-auto', fullView ? 'p-4' : 'p-6')}>
          {children}
        </main>
      </div>

      {/* Floating toggle button - top left in full view, bottom left otherwise */}
      {fullView && (
        <Button
          variant="outline"
          size="icon"
          onClick={() => setFullView(!fullView)}
          className={cn(
            'fixed z-50 shadow-lg transition-all',
            fullView
              ? 'top-3 left-3 h-10 w-10'
              : 'bottom-4 left-4 h-9 w-9'
          )}
          aria-label={fullView ? 'Show navigation' : 'Full view mode'}
        >
          <PanelLeft className="h-4 w-4" />
        </Button>
      )}
    </div >
  );
}
